import { Currency } from '@seapoint/server/enums';
import { z } from 'zod';
import { getMinPaymentAmountErrorMessage } from '../../../../utils/get-min-payment-amount-error-message';

const MODULR_SPECIAL_CHARS = /[-.\s&/,:]*/g;

// See min and max payment amount in Modulr API docs
// https://modulr.readme.io/reference/createpayments
const MINIMUM_PAYMENT_AMOUNT = 0.01;
const MAXIMUM_PAYMENT_AMOUNT = 2147483647;

export const createInvoicePaymentInformationFormSchema = (currency?: Currency, balance?: number) =>
  z.object({
    amount: z.coerce
      .number({ message: 'Payment amount is required' })
      .min(MINIMUM_PAYMENT_AMOUNT, { message: getMinPaymentAmountErrorMessage(currency) })
      .max(balance ?? MAXIMUM_PAYMENT_AMOUNT, { message: 'Payment amount must not exceed the available balance' })
      .max(MAXIMUM_PAYMENT_AMOUNT, { message: 'Payment amount must not exceed 2,147,483,647' }),
    sourceAccountId: z.string(),
    beneficiaryId: z.string().min(1, { message: 'Beneficiary is required' }),
    paymentReference: z.string().refine(
      // First argument of .refine is the validation function
      // Truthy passes the validation, falsy fails the validation
      // https://zod.dev/?id=refine
      (str) => {
        // Min 6 to max 18 characters that are not all the same (up to 140 characters for currencies other than GBP).
        // https://modulr.readme.io/reference/sendpayment
        const hasAllSameChars = new Set(str).size === 1;

        if (hasAllSameChars) {
          return false;
        }

        // Remove special characters before checking the minimum length requirements
        const strippedStr = str.replace(MODULR_SPECIAL_CHARS, '');

        // Payment reference must consist of at least 6 alphanumeric characters that are not all the same. Optional, uncounted characters include space, hyphen(-), full stop (.), ampersand(&), forward slash (/), comma(,) and colon(:). Total of all characters must be less than 140 for IBAN payment and 18 for SCAN payment.
        // https://modulr.readme.io/reference/sendpayment
        const validCharPattern = /^[a-zA-Z0-9-.&/\s]*$/;

        if (!validCharPattern.test(strippedStr)) {
          return false;
        }

        // Min 6 to max 18 characters that are not all the same (up to 140 characters for currencies other than GBP).
        // At least 6 characters must be alphanumeric and the total length can be up to 140 characters.
        // We check if the stripped string is at least 6 characters and the original string is 140 characters or fewer.
        // https://modulr.readme.io/reference/sendpayment
        return (
          (currency !== Currency.GBP && strippedStr.length >= 6 && str.length <= 140) ||
          (currency === Currency.GBP && strippedStr.length >= 6 && str.length <= 18)
        );
      },
      // Second argument runs when the validation fails
      (val) => {
        // Remove special characters before checking length requirements
        const strippedVal = val.replace(MODULR_SPECIAL_CHARS, '');

        return {
          message:
            strippedVal.length < 6
              ? 'Payment reference must be at least 6 alphanumeric characters'
              : new Set(strippedVal).size === 1
                ? 'Payment reference cannot contain all the same alphanumeric characters'
                : !/^[a-zA-Z0-9-.&/\s]*$/.test(strippedVal)
                  ? 'Payment reference can only contain letters, numbers, -, ., &, / and spaces'
                  : currency !== Currency.GBP && val.length > 140
                    ? 'Payment reference must not exceed 140 alphanumeric characters'
                    : 'Payment reference must not exceed 18 alphanumeric characters',
        };
      },
    ),
  });
